import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Users, Sparkles, Heart, ArrowRight, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import FeaturedAuthorsCarousel from '../components/landing/FeaturedAuthorsCarousel';

/**
 * About — public marketing page describing what Seven Chapter Books is,
 * what we do for readers and authors, and how visitors can get involved.
 *
 * Tone: warm, human, professional. No AI-feature mentions — every reference
 * to reviews, recommendations, and discussions reads as if curated by humans
 * (which preserves the marketing positioning the founder requested).
 */

const PILLARS = [
  {
    icon: BookOpen,
    title: 'A curated catalog',
    body: 'We work directly with independent authors and small publishers to build a hand-picked library of e-books and audiobooks across fiction, non-fiction, and everything in between. Every title is chosen because it deserves an audience.',
  },
  {
    icon: Users,
    title: 'A real reading community',
    body: 'Every book has its own live discussion room where readers swap reactions, share favorite passages, and meet other people who love the same stories. Connection beats isolation — especially for readers.',
  },
  {
    icon: Sparkles,
    title: 'Tools that respect your time',
    body: 'Reading streaks, achievements, a personal leaderboard, smart "what to read next" suggestions, and progress that syncs across all your devices. Casual reading turns into a tracked, rewarding habit without the work.',
  },
  {
    icon: Heart,
    title: 'Built for authors, too',
    body: 'Authors get a transparent royalty dashboard, downloadable per-book PDF reports, featured launch slots for press coverage, and a direct line to their readers through the discussion rooms — at terms designed to be fair.',
  },
];

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black text-white" data-testid="about-page">
      {/* Atmospheric backdrop */}
      <div className="fixed inset-0 -z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-fuchsia-700/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-amber-600/10 rounded-full blur-3xl" />
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 pt-24 sm:pt-32 pb-20">
        {/* Hero */}
        <section className="mb-20 sm:mb-28">
          <p className="text-fuchsia-400 text-xs uppercase tracking-[0.25em] font-semibold mb-3" data-testid="about-eyebrow">About Seven Chapter Books</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5" data-testid="about-headline">
            A reading platform built<br />for the people who actually <em className="text-fuchsia-300 not-italic">read</em>.
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-3xl" data-testid="about-subhead">
            Seven Chapter Books is a curated home for e-books and audiobooks from independent authors —
            a quieter alternative to the noise of the big stores. We pair every title with the things
            that actually make reading more rewarding: thoughtful reviews, a live discussion room with
            other readers, and tools that turn casual reading into a tracked habit.
          </p>
        </section>

        {/* Pillars */}
        <section className="mb-20 sm:mb-28">
          <p className="text-fuchsia-400 text-xs uppercase tracking-[0.25em] font-semibold mb-3">What we offer</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
            Built around four ideas
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {PILLARS.map(({ icon: Icon, title, body }, i) => (
              <article
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-7 hover:border-fuchsia-400/40 transition-colors"
                data-testid={`about-pillar-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-fuchsia-500/15 text-fuchsia-300 inline-flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold mb-2.5">{title}</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Story */}
        <section className="mb-20 sm:mb-28 max-w-3xl">
          <p className="text-fuchsia-400 text-xs uppercase tracking-[0.25em] font-semibold mb-3">Our story</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-testid="about-story-heading">A calmer place for readers</h2>
          <div className="space-y-5 text-gray-300 text-base sm:text-lg leading-relaxed">
            <p>
              Seven Chapter Books started with a simple frustration: the largest e-book stores have everything,
              and yet finding a book you&apos;ll actually love feels harder every year. Algorithms surface what&apos;s
              loud, not what&apos;s good. Discussion lives on whichever social network is currently popular, and
              authors spend more time chasing platforms than writing.
            </p>
            <p>
              We wanted something different — a single, calmer place where every title is intentionally chosen,
              where readers can talk to other readers about the book in real time, and where the authors who
              wrote those books get paid fairly and stay close to their audience. That&apos;s the platform we&apos;re
              building, one book at a time.
            </p>
            <p>
              We&apos;re a small, independent team. Every reader who joins, every author we publish, and every
              piece of feedback you send us shapes what we do next.
            </p>
          </div>
        </section>

        {/* Featured Authors */}
        <FeaturedAuthorsCarousel />

        {/* CTA */}
        <section className="rounded-3xl bg-gradient-to-br from-fuchsia-700/15 via-zinc-900/60 to-amber-700/10 border border-white/10 p-8 sm:p-12 text-center" data-testid="about-cta">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Read with us</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-7">
            Join Seven Chapter Books to unlock the full catalog, the discussion rooms, and the tools that make
            reading more rewarding. Authors and publishers — we&apos;d love to hear from you too.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => navigate('/login')}
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold px-6 py-5 rounded-lg"
              data-testid="about-cta-start"
            >
              Start reading
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <a
              href="mailto:info@sevenchapterbooks.com?subject=Hello"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-medium px-6 py-3.5 rounded-lg transition-colors"
              data-testid="about-cta-contact"
            >
              <Mail className="h-4 w-4" />
              Contact us
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default About;
